import { useState, useCallback } from 'react';
import { Loader2, RefreshCw, Eye, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { statementPayoutMatchingAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const BUCKET_META = [
  ['matched_exact', 'Matched exact', 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'],
  ['amount_mismatch', 'Amount mismatch', 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'],
  ['statement_no_candidate', 'Statement, no candidate txn', 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'],
  ['txn_no_candidate', 'CWC txn, no candidate statement', 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'],
];

// Consistency Census + Preview/Apply for CWC statement-payout reconciliation
// (see services/statement_payout_matcher.py). Synchronous, not a chunked
// chain -- both cohorts are provably bounded (CLAUDE.md section 13), so
// there is no job-polling here, just a direct request/response.
export function StatementPayoutMatchingCard() {
  const [census, setCensus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingCensus, setLoadingCensus] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [partial, setPartial] = useState(null);
  const [loadingPartial, setLoadingPartial] = useState(false);
  const [applyingPartial, setApplyingPartial] = useState(false);

  const loadCensus = useCallback(async () => {
    setLoadingCensus(true);
    try {
      const { data } = await statementPayoutMatchingAPI.getCensus();
      setCensus(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingCensus(false);
    }
  }, []);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const { data } = await statementPayoutMatchingAPI.getPreview();
      setPreview(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingPreview(false);
    }
  }, []);

  const apply = useCallback(async () => {
    if (!preview?.proposed_matches?.length) return;
    setApplying(true);
    try {
      const { data } = await statementPayoutMatchingAPI.apply(preview.proposed_matches);
      toast.success(`Applied ${data.applied.length} match(es)${data.skipped.length ? `, skipped ${data.skipped.length}` : ''}`);
      setPreview(null);
      await loadCensus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  }, [preview, loadCensus]);

  const loadPartial = useCallback(async () => {
    setLoadingPartial(true);
    try {
      const { data } = await statementPayoutMatchingAPI.getPartialPreview();
      setPartial(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoadingPartial(false);
    }
  }, []);

  const applyPartial = useCallback(async () => {
    if (!partial?.proposed?.length) return;
    setApplyingPartial(true);
    try {
      const { data } = await statementPayoutMatchingAPI.applyPartial(partial.proposed);
      toast.success(
        `Part-reconciled ${data.applied.length} statement(s)` +
        (data.skipped.length ? `, skipped ${data.skipped.length}` : ''),
      );
      setPartial(null);
      await loadCensus();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyingPartial(false);
    }
  }, [partial, loadCensus]);

  const counts = census?.bucket_counts || {};
  const total = (census?.statement_count || 0) + (census?.cwc_txn_count || 0);
  const sumCheck = Object.entries(counts).reduce(
    (acc, [k, v]) => acc + v * (k === 'matched_exact' || k === 'amount_mismatch' ? 2 : 1), 0,
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>CWC Statement Payout Reconciliation</CardTitle>
        <Button variant="outline" size="sm" onClick={loadCensus} disabled={loadingCensus}>
          {loadingCensus ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Run census
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {!census && !loadingCensus && (
          <p className="text-sm text-muted-foreground">
            Run the census to see how CWC bank transactions reconcile against parsed management statements.
          </p>
        )}

        {census && (
          <div>
            <p className="text-sm text-muted-foreground mb-2">
              {fmtInt(census.statement_count)} CWC statement(s), {fmtInt(census.cwc_txn_count)} CWC transaction(s)
              {sumCheck === total ? '' : ' -- WARNING: bucket counts do not sum to statements + transactions'}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {BUCKET_META.map(([key, label, className]) => (
                <div key={key} className={`rounded-md p-2 ${className}`}>
                  <div className="text-xs font-medium">{label}</div>
                  <div className="text-lg font-mono">{fmtInt(counts[key])}</div>
                </div>
              ))}
            </div>

            {counts.amount_mismatch > 0 && (
              <div className="mt-4 overflow-x-auto">
                <div className="text-xs font-medium mb-1">Amount mismatches (not 100% consistent)</div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="py-1 pr-2">Statement</th>
                      <th className="py-1 pr-2">Period</th>
                      <th className="py-1 pr-2 text-right">Expected</th>
                      <th className="py-1 pr-2 text-right">Actual</th>
                      <th className="py-1 pr-2 text-right">Delta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {census.buckets.amount_mismatch.map((m) => (
                      <tr key={`${m.statement_id}-${m.txn_id}`} className="border-t">
                        <td className="py-1 pr-2 font-mono">{m.statement_id}</td>
                        <td className="py-1 pr-2">{m.period}</td>
                        <td className="py-1 pr-2 text-right font-mono">{m.expected}</td>
                        <td className="py-1 pr-2 text-right font-mono">{m.actual}</td>
                        <td className="py-1 pr-2 text-right font-mono text-red-600">{m.delta}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 pt-2 border-t">
          <Button variant="outline" size="sm" onClick={loadPreview} disabled={loadingPreview}>
            {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
            Preview matches
          </Button>
          {preview && (
            <Button size="sm" onClick={apply} disabled={applying || !preview.proposed_matches?.length}>
              {applying ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Apply {preview.count || 0} match(es)
            </Button>
          )}
        </div>

        {preview && (
          <div className="overflow-x-auto">
            {preview.proposed_matches.length === 0 ? (
              <p className="text-sm text-muted-foreground">No proposed matches -- nothing new to apply.</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1 pr-2">Statement</th>
                    <th className="py-1 pr-2">Period</th>
                    <th className="py-1 pr-2 text-right">Payout</th>
                    <th className="py-1 pr-2">Txn date</th>
                    <th className="py-1 pr-2">Txn description</th>
                    <th className="py-1 pr-2">Counterparty resolution</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.proposed_matches.map((m) => (
                    <tr key={`${m.statement_id}-${m.txn_id}`} className="border-t">
                      <td className="py-1 pr-2 font-mono">{m.statement_id}</td>
                      <td className="py-1 pr-2">{m.period}</td>
                      <td className="py-1 pr-2 text-right font-mono">{m.payout_amount}</td>
                      <td className="py-1 pr-2">{m.txn_date}</td>
                      <td className="py-1 pr-2">{m.txn_description}</td>
                      <td className="py-1 pr-2">
                        <Badge variant="outline" className="text-[10px] px-1 py-0">{m.counterparty_resolution_reason}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Part-reconciliation: CWC paid, but not the statement's amount.
            Settling the pair anyway (with the difference recorded as an
            overpayment/underpayment chip) is what stops both the statement
            and the bank line sitting open forever. */}
        <div className="pt-2 border-t space-y-2" data-testid="partial-payout-section">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadPartial} disabled={loadingPartial} data-testid="partial-preview-btn">
              {loadingPartial ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
              Preview part-reconciliations
            </Button>
            {partial && (
              <Button size="sm" onClick={applyPartial} disabled={applyingPartial || !partial.proposed?.length} data-testid="partial-apply-btn">
                {applyingPartial ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                Part-reconcile {partial.counts?.proposed || 0}
              </Button>
            )}
          </div>

          {partial && (
            <div className="overflow-x-auto">
              {partial.proposed.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing to part-reconcile.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="py-1 pr-2">Statement</th>
                      <th className="py-1 pr-2">Period</th>
                      <th className="py-1 pr-2">Property</th>
                      <th className="py-1 pr-2 text-right">Expected</th>
                      <th className="py-1 pr-2 text-right">Paid</th>
                      <th className="py-1 pr-2 text-right">Difference</th>
                      <th className="py-1 pr-2">Txn date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partial.proposed.map((m) => (
                      <tr key={`${m.statement_id}-${m.txn_id}`} className="border-t">
                        <td className="py-1 pr-2 font-mono">{m.statement_id}</td>
                        <td className="py-1 pr-2">{m.period}</td>
                        <td className="py-1 pr-2">{m.property_name || '-'}</td>
                        <td className="py-1 pr-2 text-right font-mono">{m.expected}</td>
                        <td className="py-1 pr-2 text-right font-mono">{m.actual}</td>
                        <td className="py-1 pr-2 text-right">
                          <Badge
                            className={`text-[10px] px-1 py-0 border-0 ${
                              m.variance_direction === 'exact'
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
                                : m.variance_direction === 'overpayment'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400'
                            }`}
                          >
                            {m.variance_direction === 'exact'
                              ? 'Exact'
                              : `${m.variance_direction === 'overpayment' ? 'Overpaid' : 'Underpaid'} ${m.variance}`}
                          </Badge>
                        </td>
                        <td className="py-1 pr-2">{String(m.txn_date || '').slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {partial.out_of_tolerance?.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {partial.out_of_tolerance.length} pair(s) are too far apart to settle automatically and need a
                  human call -- they are listed by the API under out_of_tolerance, never silently matched.
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
